import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, RotateCcw, Trash2, Archive } from "lucide-react";
import { api } from "@/lib/api";
import { Pill, FilterSelect, DateInput, Pager, DARK_INPUT, inr, errMsg, useDebounced, useLatest } from "./shared";
import BookingDetailSheet from "./BookingDetailSheet";

const STATUSES = ["pending_review", "contacted", "scheduled_offline", "created", "finding_workers", "partially_assigned", "fully_assigned", "in_progress", "work_started", "completed", "cancelled", "on_hold", "declined", "closed"];
const PAY_STATUSES = ["not_collected", "pending", "processing", "advance_paid", "fully_paid", "successful", "failed", "partially_refunded", "refunded"];
const CATEGORIES = ["loading_unloading", "daily_labour", "tiles_work", "house_repair"];
const EMPTY = { q: "", status: "", payment_status: "", category: "", date_from: "", date_to: "" };

export default function BookingManagement() {
  const [view, setView] = useState("active");
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const q = useDebounced(f.q);
  const begin = useLatest();

  const load = useCallback(async () => {
    const isLatest = begin();
    try {
      const params = Object.fromEntries(Object.entries({ ...f, q, view, page, page_size: 20 }).filter(([, v]) => v !== ""));
      const { data } = await api.get("/admin/manage/bookings", { params });
      if (isLatest()) setData(data);
    } catch (e) { if (isLatest()) { toast.error(errMsg(e, "Failed to load bookings")); setData({ items: [], total: 0, counts: {} }); } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.status, f.payment_status, f.category, f.date_from, f.date_to, q, view, page]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };

  return (
    <div data-testid="booking-management-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold">Admin · Control</div>
          <h1 className="font-display text-3xl font-bold mt-1">Booking Management</h1>
          <p className="text-sm text-slate-400 mt-1">Search, edit, archive and restore every booking. Permanent deletion is reserved for the Primary Super Admin.</p>
        </div>
        <div className="flex rounded-lg border border-slate-700 overflow-hidden" data-testid="bm-view-toggle">
          {[["active", "Active", Archive], ["deleted", "Deleted", Trash2]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => { setView(k); setPage(1); }} data-testid={`bm-view-${k}`}
              className={`px-4 py-2 text-sm flex items-center gap-2 transition-colors ${view === k ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-400 hover:text-slate-100"}`}>
              <Icon size={14}/>{label}<span className="font-mono text-xs opacity-80">{data?.counts?.[k] ?? "–"}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="admin-card p-3 mb-4 flex flex-col md:flex-row md:flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
          <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Code, customer, phone, location…" className={`${DARK_INPUT} pl-9 h-9`} data-testid="bm-search-input"/>
        </div>
        <FilterSelect value={f.status} onChange={set("status")} options={STATUSES} placeholder="All statuses" testid="bm-filter-status"/>
        <FilterSelect value={f.payment_status} onChange={set("payment_status")} options={PAY_STATUSES} placeholder="All payments" testid="bm-filter-payment"/>
        <FilterSelect value={f.category} onChange={set("category")} options={CATEGORIES} placeholder="All services" testid="bm-filter-category"/>
        <DateInput value={f.date_from} onChange={set("date_from")} testid="bm-filter-from"/>
        <DateInput value={f.date_to} onChange={set("date_to")} testid="bm-filter-to"/>
        <Button variant="ghost" size="sm" className="text-slate-400 h-9" onClick={() => { setF(EMPTY); setPage(1); }} data-testid="bm-filter-reset"><RotateCcw size={14} className="mr-1"/>Reset</Button>
      </div>

      <BookingTable items={data?.items} onOpen={setOpenId}/>
      {data && <Pager page={page} pageSize={20} total={data.total} onPage={setPage} testid="bm-pager"/>}
      <BookingDetailSheet id={openId} onClose={() => setOpenId(null)} onChanged={load}/>
    </div>
  );
}

function BookingTable({ items, onOpen }) {
  if (!items) return <div className="text-slate-500 text-sm p-6">Loading…</div>;
  if (!items.length) return <div className="admin-card p-10 text-center text-slate-500" data-testid="bm-empty">No bookings match these filters.</div>;
  return (
    <div className="admin-card overflow-x-auto">
      <table className="w-full text-sm min-w-[820px]">
        <thead className="bg-slate-800/70 text-slate-400 text-[10px] uppercase tracking-widest">
          <tr><th className="p-3 text-left">Booking</th><th className="text-left">Customer</th><th className="text-left">Service</th><th className="text-left">Scheduled</th><th className="text-left">Status</th><th className="text-left">Payment</th><th className="text-right pr-4">Amount</th></tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.id} onClick={() => onOpen(b.id)} data-testid={`bm-row-${b.code}`}
              className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer transition-colors">
              <td className="p-3"><div className="font-mono font-bold text-orange-300">{b.code}</div><div className="text-[11px] text-slate-500">{b.mode === "request_only" ? "Request" : "Paid flow"}</div></td>
              <td><div className="text-slate-200">{b.customer_name}</div><div className="text-[11px] text-slate-500 font-mono">{b.customer_phone}</div></td>
              <td className="text-slate-300">{b.pricing?.service_label || b.category}<div className="text-[11px] text-slate-500">{b.worker_count} worker(s)</div></td>
              <td className="text-slate-300 font-mono text-xs">{b.scheduled_date}<div className="text-slate-500">{b.start_time}</div></td>
              <td>{b.is_deleted ? <Pill value="deleted"/> : <Pill value={b.status} testid={`bm-status-${b.code}`}/>}</td>
              <td><Pill value={b.payment_status}/></td>
              <td className="text-right pr-4 font-mono font-bold text-emerald-400">{inr(b.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
